/**
 * Top-level routing for apps/web's surfaces (implementation-brief §12).
 *
 * No router dependency: there are five routes, decided once from `window.location`, so a
 * small hand-rolled split is more honest than a library neither this task nor the brief
 * names. `apps/web/public/_redirects` makes the Netlify-hosted deploy serve `index.html`
 * for every one of them.
 *
 * `/privacy` and `/support` are the two URLs App Store Connect requires
 * (`docs/marketing/app-store-listing.md` §URLs, which names `apps/web` as their host), and
 * `/terms` is the founder-directed third prose page (`docs/DEVIATIONS.md` **W3**).
 * All three are checked BEFORE the `/list` test and before the marketing fallback, and they
 * are exact-match rather than prefix-match: an unknown path should 404 rather than be
 * quietly served a privacy policy, which is the same reasoning `_redirects` gives for
 * enumerating paths instead of using a catch-all.
 *
 * `/list` has three shapes: two of them since RULINGS **R48**, and the first one since
 * RULINGS **R67**.
 *
 *   /list#t=<token>   THE SHAPE AN OWNER'S LINK ACTUALLY HAS. The token is in the URL
 *                     **fragment**, which a browser never transmits to any server — not in
 *                     the request line, not in a `Referer`. R67: "The link an owner shares
 *                     carries the token in the URL fragment... which a browser never
 *                     transmits to any server."
 *   /list?t=<token>   the same thing in a query parameter. STILL READ, for compatibility
 *                     with any link minted before R67, and the WEAKER form: a query string
 *                     is sent in full and is therefore in the platform's access log.
 *                     `functions/handlers/shopping-lists.ts` no longer mints this shape.
 *   /list?id=<uuid>   the local preview, with no server behind it. An id is minted if
 *                     none is given, so the page is still shareable BETWEEN TABS on the
 *                     one device — and the page says plainly that this is all it is
 *                     (`share.localOnly`).
 *
 * ## Why the fragment is not stripped from the address bar
 *
 * The token is read and never rewritten: `history.replaceState` is used for the preview
 * id only. Stripping the token from the address bar would look tidier and would break the
 * page on reload, which is the wrong trade for a link somebody was sent. R67 acknowledges
 * the residual: "a fragment is invisible to the server but not to the user's history" —
 * which is why `no-referrer`, `no-store` and `noindex` all stay on this route (see
 * `index.html`'s referrer meta and `public/_headers`).
 */
import { useEffect, useState } from 'react';
import { MarketingPage } from './marketing/MarketingPage';
import { PrivacyPage } from './site/PrivacyPage';
import { SupportPage } from './site/SupportPage';
import { TermsPage } from './site/TermsPage';
import { SharedListPage } from './list/SharedListPage';
import { PENDING_LIST_ID } from './list/backend';
import { readShareToken } from './list/shareToken';

function readParam(name: string): string | null {
  if (typeof window === 'undefined') return null;
  return new URL(window.location.href).searchParams.get(name);
}

/** The path, without a trailing slash, so `/privacy/` and `/privacy` are one route. */
function pathname(): string {
  if (typeof window === 'undefined') return '/';
  const raw = window.location.pathname;
  return raw.length > 1 && raw.endsWith('/') ? raw.slice(0, -1) : raw;
}

export function App() {
  const path = pathname();
  const isListRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/list');
  // Fragment first, query second — R67, and the rule lives in `list/shareToken.ts` so it can
  // be tested as a pure function rather than only by rendering this component.
  const [token] = useState<string | null>(() =>
    isListRoute && typeof window !== 'undefined' ? readShareToken(window.location.href) : null,
  );
  const [previewListId, setPreviewListId] = useState<string | null>(() =>
    isListRoute ? readParam('id') : null,
  );

  // A preview link with no id yet (someone typed /list, or this is the demo entry point)
  // mints one and puts it in the address bar, so the page itself becomes the shareable
  // link. Deliberately skipped when a token is present: a capability URL already names
  // its list, and adding an `id` next to it would suggest the id was doing something.
  useEffect(() => {
    if (!isListRoute || token || previewListId) return;
    const id = crypto.randomUUID();
    const url = new URL(window.location.href);
    url.searchParams.set('id', id);
    window.history.replaceState(null, '', url.toString());
    setPreviewListId(id);
  }, [isListRoute, token, previewListId]);

  if (path === '/privacy') return <PrivacyPage />;
  if (path === '/support') return <SupportPage />;
  if (path === '/terms') return <TermsPage />;

  if (isListRoute) {
    if (token) return <SharedListPage token={token} previewListId={PENDING_LIST_ID} />;
    return previewListId ? <SharedListPage token={null} previewListId={previewListId} /> : null;
  }
  return <MarketingPage />;
}
